import { useMemo, useState } from 'react'
import type { IStoreSelectedFacet } from '@faststore/api'
import { ChevronDown } from 'src/components/Icons/ChevronDown'
import { ChevronUp } from 'src/components/Icons/ChevronUp'

import { Accordion } from './Accordion'
import { Checkbox } from './Checkbox'
import type { Facet, VariantTypeCheckbox } from './types'
import { sortFacetValues } from './utils/facets'

interface Props extends FacetContentProps {
  useAccordion: boolean
}

interface FacetContentProps {
  facet: Facet
  checkboxType: VariantTypeCheckbox
  onFacetChange: (item: IStoreSelectedFacet, type?: 'RANGE' | 'BOOLEAN') => void
  defaultNumberOpts?: number
  Icon?: JSX.Element
}

export function FacetSection({
  facet,
  checkboxType,
  useAccordion = false,
  defaultNumberOpts = 6,
  onFacetChange,
  Icon,
}: Readonly<Props>) {
  const selectedValues = useMemo(() => {
    return facet.values.filter((value) => value.selected)
  }, [facet])

  return useAccordion ? (
    <Accordion
      title={facet.name}
      index={facet.key}
      selectedValues={selectedValues}
      showSelectedValues={false}
      Icon={Icon}
    >
      <FacetSectionContent
        facet={facet}
        checkboxType={checkboxType}
        onFacetChange={onFacetChange}
        defaultNumberOpts={defaultNumberOpts}
      />
    </Accordion>
  ) : (
    <div className="border-b border-gray py-6">
      <span className="flex items-center gap-2 text-base text-restructure-primary font-medium font-inter text-left leading-[21px]">
        {!!Icon && Icon}
        {facet.name}
      </span>
      <FacetSectionContent
        facet={facet}
        checkboxType={checkboxType}
        onFacetChange={onFacetChange}
        defaultNumberOpts={defaultNumberOpts}
      />
    </div>
  )
}

const FacetSectionContent = ({
  facet,
  checkboxType,
  onFacetChange,
  defaultNumberOpts = 6,
}: FacetContentProps) => {
  const [isCollapse, setIsCollapse] = useState(false)

  const activeShowMoreButton = facet.values.length > defaultNumberOpts

  const options = useMemo(() => {
    const { values, name } = facet

    const sortedValues = sortFacetValues(values, name)

    if (!isCollapse) {
      return sortedValues.slice(0, defaultNumberOpts)
    }

    return sortedValues
  }, [defaultNumberOpts, facet, isCollapse])

  return (
    <div className="mt-4">
      <div className="flex flex-wrap items-center gap-[6px]">
        {options.map((value) => {
          return (
            <Checkbox
              type={checkboxType}
              key={value.value}
              facet={value}
              onFacetChange={onFacetChange}
            />
          )
        })}
        {activeShowMoreButton && (
          <button
            onClick={() => setIsCollapse(!isCollapse)}
            data-store-label
            data-testid="store-label"
            className="flex gap-1 justify-between sm:justify-start px-4 py-3 text-sm font-medium items-center border rounded-round border-restructure-background-neutral-5 text-[#000]"
          >
            {isCollapse ? 'Fechar' : 'Ver todos'}
            {isCollapse ? <ChevronUp /> : <ChevronDown />}
          </button>
        )}
      </div>
    </div>
  )
}
